import React from 'react';
import { Card, Col, Row } from 'antd';
import { Link } from 'react-router-dom';
import moment from 'moment';
import cx from 'classnames';
import { isNil } from 'lodash';

import {
  AssignmentStatusTypeEnum,
  AssignmentTemplateSummaryResponseModel,
  AssignmentTitleInfoModel,
  RenewKindEnum,
  RenewStatusEnum,
} from 'api/backofficeApiClient';
import { AssignmentInfoTypes } from '../enums';
import { getFullName } from 'utils/personUtils';
import { getLocation } from 'utils/locationUtils';
import {
  getAssignmentStatusClassName,
  getAssignmentStatusLabel,
  getAssignmentTypeLabel,
} from 'utils/assignmentUtils';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { useAppSelector } from 'hooks/useAppSelector';
import BaseLabel from 'components/wrappers/BaseLabel';
import RenewStatusLabel from 'components/RenewStatusLabel';

type Props = {
  className?: string;
  assignment: AssignmentTitleInfoModel | AssignmentTemplateSummaryResponseModel;
  isTemplate?: boolean;
  isPartner?: boolean;
  shouldDisplayStatus?: boolean;
};

export default function AssignmentCard(props: Props) {
  const {
    assignment,
    className = '',
    isTemplate,
    isPartner,
    shouldDisplayStatus = true,
  } = props;
  const isAdminRole = useAppSelector(isAdminSelector);
  const {
    assignmentType,
    customerInfo,
    memberKey,
    memberCompanyName,
    workLocation,
    billingLocation,
    jobTitle,
    jobStartDate,
    jobEndDate,
    workerInfo,
  } = assignment;

  const { templateName, id: templateId } =
    assignment as AssignmentTemplateSummaryResponseModel;

  const {
    assignmentId,
    assignmentStatus,
    assignmentKey,
    renewStatus,
    renewKind,
    parentAssignmentId,
    parentAssignmentKey,
    renewType,
  } = assignment as AssignmentTitleInfoModel;

  const billingAddress = getLocation(
    billingLocation?.country,
    billingLocation?.address,
    billingLocation?.city || billingLocation?.internationalCityName,
    billingLocation?.state || billingLocation?.internationalStateName,
    billingLocation?.zipCode,
  );
  const workAddress = getLocation(
    workLocation?.country,
    workLocation?.address,
    workLocation?.city || workLocation?.internationalCityName,
    workLocation?.state || workLocation?.internationalStateName,
    workLocation?.zipCode,
  );
  const workerFullName = getFullName(
    workerInfo?.firstName,
    workerInfo?.lastName,
  );
  const assignmentTitle =
    assignmentStatus === AssignmentStatusTypeEnum.Draft
      ? 'Draft assignment'
      : assignmentKey;
  const isRehireKind = renewKind === RenewKindEnum.Rehire;
  const isRenewKind = renewKind === RenewKindEnum.Renew;

  const getCorrectLink = () => {
    if (isTemplate)
      return (
        <Link className="card-title" to={`/assignment-template/${templateId}`}>
          {templateName}
        </Link>
      );

    if (isPartner)
      return (
        <Link
          className="card-title"
          to={`/assignment-partner/${assignmentKey}`}
        >
          {assignmentTitle}
        </Link>
      );

    return (
      <Link
        className="card-title"
        to={`/assignment/${assignmentStatus === AssignmentStatusTypeEnum.Draft
          ? assignmentId
          : assignmentKey
          }?section=${AssignmentInfoTypes.Primary}`}
      >
        {assignmentTitle}
      </Link>
    );
  };

  return (
    <Card
      className={cx('card-item stretch-card', className)}
      data-id={
        isTemplate
          ? `assignment-template-card-${templateId}`
          : `assignment-card-${assignmentId}`
      }
    >
      <div className="card-inner">
        <div className="inner-text-box">
          <Row justify="space-between" wrap={false} gutter={10}>
            <Col >{getCorrectLink()}</Col>

            <Col>
              <Row gutter={[10, 10]} justify="end">
                {isTemplate && (
                  <Col>
                    <BaseLabel status="Template" />
                  </Col>
                )}

                <Col>
                  <RenewStatusLabel
                    renewStatus={renewStatus}
                    renewKind={renewKind}
                    renewType={renewType}
                  />
                </Col>

                {isRenewKind && renewStatus === RenewStatusEnum.RenewRequest && (
                  <Col>
                    <div className="card-status">
                      <span className="icon icon-exchange-status" />
                      Renewal request
                    </div>
                  </Col>
                )}

                {isRehireKind && renewStatus === RenewStatusEnum.RenewRequest && (
                  <Col>
                    <div className="card-status">
                      <span className="icon icon-exchange-status" />
                      Rehire request
                    </div>
                  </Col>
                )}

                {shouldDisplayStatus && !isTemplate && !isNil(assignmentStatus) && (
                  <Col>
                    <BaseLabel
                      status={getAssignmentStatusLabel(assignmentStatus)}
                      statusClassName={getAssignmentStatusClassName(
                        assignmentStatus,
                      )}
                    />
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </div>
      </div>

      <Row gutter={[20, 10]} className="titled-sheet">
        {!!assignmentType && (
          <Col xs={24} md={12} data-id="assignment-type">
            <div className="title">Assignment type: </div>
            <div>{getAssignmentTypeLabel(assignmentType)}</div>
          </Col>
        )}

        {!!parentAssignmentId && !!parentAssignmentKey && (
          <Col xs={24} md={12} data-id="requestRelatedTo">
            <div className="title">Related to:</div>

            {isPartner ? (
              parentAssignmentKey
            ) : (
              <Link
                className="card-link"
                to={`/assignment/${parentAssignmentKey}?section=${AssignmentInfoTypes.Primary}`}
              >
                <span>{parentAssignmentKey}</span>
              </Link>
            )}
          </Col>
        )}

        {!!memberCompanyName && (
          <Col xs={24} md={12} data-id="assignment-member-company">
            <div className="title">Member company: </div>

            {isAdminRole ? (
              <div>
                <Link to={`/member/${memberKey}`}>{memberCompanyName}</Link>
              </div>
            ) : (
              <div>{memberCompanyName}</div>
            )}
          </Col>
        )}

        {!!workerFullName && (
          <Col xs={24} md={12} data-id="assignment-worker">
            <div className="title">Worker:</div>

            {!isPartner && workerInfo?.workerKey ? (
              <Link
                className="card-link"
                to={`/worker/${workerInfo?.workerKey}`}
              >
                {workerFullName}
              </Link>
            ) : (
              <div>{workerFullName}</div>
            )}
          </Col>
        )}

        {!!jobTitle && (
          <Col xs={24} md={12} data-id="job-title">
            <div className="title">Job title:</div>

            <div className="card-link">{jobTitle}</div>
          </Col>
        )}

        {!!jobStartDate && (
          <Col xs={24} md={12} data-id="assignment-start-date">
            <div className="title">Start date: </div>
            <div>{moment.utc(jobStartDate).format('MM/DD/YYYY')}</div>
          </Col>
        )}

        {!!jobEndDate && (
          <Col xs={24} md={12} data-id="assignment-end-date">
            <div className="title">End date: </div>
            <div>{moment.utc(jobEndDate).format('MM/DD/YYYY')}</div>
          </Col>
        )}

        {!!workAddress && (
          <Col xs={24} md={12} data-id="assignment-work-location">
            <div className="title">Work location: </div>
            <div>{workAddress}</div>
          </Col>
        )}

        {!!billingAddress && (
          <Col xs={24} md={12} data-id="assignment-billing-location">
            <div className="title">Billing location: </div>
            <div>{billingAddress}</div>
          </Col>
        )}

        {!!customerInfo?.companyName && (
          <Col xs={24} md={12} data-id="assignment-customer-company">
            <div className="title">Customer company name: </div>
            <div>{customerInfo?.companyName}</div>
          </Col>
        )}
      </Row>
    </Card >
  );
}
